import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';

interface CreateRecordTool {
  name: AgentTools.sf_create_record;
  args: {
    objectName: string;
    data: Record<string, unknown>;
  };
}

export type CreateRecordToolCall = ToolCall<CreateRecordTool['name'], CreateRecordTool['args']>;

export type CreateRecordToolCallSummary = ToolCallSummary<
  CreateRecordTool['name'],
  CreateRecordTool['args']
>;

interface CreateRecordResult {
  id: string;
  success: boolean;
  errors: string[];
}

export function CreateRecordToolCall({ toolCall }: { toolCall: CreateRecordToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<CreateRecordResult>(
    toolCall,
    ({ toolCallState }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_create_record.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.sf_create_record.title.errors');
        case ToolCallState.Success:
          return t('toolCalls.sf_create_record.title.success');
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.sf_create_record.results.tabTitle'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallDataList
              items={[
                {
                  label: t('toolCalls.sf_create_record.results.createdRecordId'),
                  value: parsedResult.results.id,
                },
              ]}
            />
          )
        ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.sf_create_record.parameters.objectName'),
              value: toolCall.args.objectName,
            },
            ...Object.entries(toolCall.args.data || {}).map(([key, value]) => ({
              label: key,
              value: value?.toString() || '',
            })),
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
